import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { FilesService } from '../data/services/files.service';
import { EMPTY, forkJoin, of, tap } from 'rxjs';
import * as AdminActions from './admin.actions';
import {
  loadCurrentBilling,
  loadCurrentBillingSuccess,
  loadSelectedCompanyBilling,
  loadSelectedCompanyBillingSuccess,
  loadStatsForPeriodAdminFailure,
} from './admin.actions';
import { catchError, map, switchMap } from 'rxjs/operators';
import { UserService } from '../data/services/user.service';
import { PaginatedUsers } from '../domain/models/user/paginated-users';
import { CompanyService } from '../data/services/company.service';
import { PaginatedCompanies } from '../domain/models/company/paginated-companies';
import { BillingService } from '../data/services/billing.service';
import { CsvService } from '../data/services/csv.service';
import { DateUtils } from '../../../common/helpers/date-utils';
import { saveAs } from 'file-saver';

@Injectable()
export class AdminEffects {
  protected dateUtils = inject(DateUtils);
  protected filesService = inject(FilesService);
  loadStats$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AdminActions.loadStatsForPeriod),
      switchMap(({ startDate, endDate }) =>
        this.filesService.getStatsForPeriod(startDate, endDate).pipe(
          map((res) =>
            AdminActions.loadStatsForPeriodSuccess({ dashboardStats: res }),
          ),
          catchError((error) =>
            of(
              AdminActions.loadStatsForPeriodFailure({
                error: error.message,
              }),
            ),
          ),
        ),
      ),
    ),
  );
  loadConvertedFiles$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AdminActions.loadConvertedFiles),
      switchMap(
        ({
          page = 1,
          pageSize = 10,
          search = '',
          fileType = '',
          sortColumn,
          sortDirection,
          startDate,
          endDate,
          companyIds,
        }) =>
          this.filesService
            .getConvertedFiles(
              page,
              pageSize,
              search,
              fileType,
              sortColumn,
              sortDirection,
              startDate,
              endDate,
              companyIds,
            )
            .pipe(
              map((res) =>
                AdminActions.loadConvertedFilesSuccess({
                  items: res.items,
                  total: res.totalCount,
                  page,
                  pageSize,
                  search,
                  fileType,
                  startDate,
                  endDate,
                  companyIds,
                }),
              ),
              catchError((error) =>
                of(
                  AdminActions.loadConvertedFilesFailure({
                    error: error.message,
                  }),
                ),
              ),
            ),
      ),
    ),
  );
  loadUserStats$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AdminActions.loadUserStatsByPeriod),
      switchMap(({ oidcId, startDate, endDate }) =>
        this.filesService
          .getUserStatsForPeriod(oidcId, startDate, endDate)
          .pipe(
            map((userStats) =>
              AdminActions.loadUserStatsByPeriodSuccess({ userStats }),
            ),
            catchError((error) =>
              of(
                AdminActions.loadUserStatsByPeriodFailure({
                  error: error.message,
                }),
              ),
            ),
          ),
      ),
    ),
  );
  protected userService = inject(UserService);
  loadUsers$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AdminActions.loadUsers),
      switchMap(
        ({
          page = 1,
          pageSize = 10,
          search = '',
          sortColumn,
          sortDirection,
          status,
          profiles,
        }) =>
          this.userService
            .getPaginatedUsers(
              page,
              pageSize,
              search,
              sortColumn,
              sortDirection,
              status,
              profiles,
            )
            .pipe(
              map((res: PaginatedUsers) =>
                AdminActions.loadUsersSuccess({
                  items: res.items,
                  total: res.totalCount,
                  page,
                  pageSize,
                  search,
                }),
              ),
            ),
      ),
    ),
  );
  loadUserById$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AdminActions.loadUserById),
      switchMap(({ id }) =>
        this.userService.getUserById(id).pipe(
          map((user) => AdminActions.loadUserByIdSuccess({ user })),
          catchError((error) =>
            of(AdminActions.loadUserByIdFailure({ error: error.message })),
          ),
        ),
      ),
    ),
  );
  protected companyService = inject(CompanyService);
  loadStatsAdmin$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AdminActions.loadStatsForPeriodAdmin),
      switchMap(({ startDate, endDate }) =>
        forkJoin({
          stats: this.filesService.getStatsForPeriod(startDate, endDate),
          companyCount: this.companyService.getCompanyCount(),
        }).pipe(
          map(({ stats, companyCount }) =>
            AdminActions.loadStatsForPeriodAdminSuccess({
              dashboardStats: {
                ...stats,
                companyCount,
              },
            }),
          ),
          catchError((error) =>
            of(
              AdminActions.loadStatsForPeriodAdminFailure({
                error: error.message,
              }),
            ),
          ),
        ),
      ),
    ),
  );
  loadCompanies$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AdminActions.loadCompanies),
      switchMap(
        ({ page = 1, pageSize = 10, search = '', sortColumn, sortDirection }) =>
          this.companyService
            .getPaginatedCompanies(
              page,
              pageSize,
              search,
              sortColumn,
              sortDirection,
            )
            .pipe(
              map((res: PaginatedCompanies) =>
                AdminActions.loadCompaniesSuccess({
                  items: res.items,
                  total: res.totalCount,
                  page,
                  pageSize,
                  search,
                }),
              ),
            ),
      ),
    ),
  );
  loadCompanyById$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AdminActions.loadCompanyById),
      switchMap(({ id }) =>
        this.companyService.getCompanyById(id).pipe(
          map((company) => AdminActions.loadCompanyByIdSuccess({ company })),
          catchError((error) =>
            of(AdminActions.loadCompanyByIdFailure({ error: error.message })),
          ),
        ),
      ),
    ),
  );
  loadCompanyStats$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AdminActions.loadCompanyStats),
      switchMap(({ id, startDate, endDate }) =>
        this.companyService.getCompanyStats(id, startDate, endDate).pipe(
          map((companyStats) =>
            AdminActions.loadCompanyStatsSuccess({ companyStats }),
          ),
          catchError((error) =>
            of(AdminActions.loadCompanyStatsFailure({ error: error.message })),
          ),
        ),
      ),
    ),
  );
  protected csvService = inject(CsvService);
  downloadCompanyStatsCsv$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AdminActions.downloadCompanyStatsCsv),
      switchMap(({ startDate, endDate }) =>
        this.csvService.getCompanyStatsForPeriodCsv(startDate, endDate).pipe(
          tap((blob) => {
            let filenameSuffix = '';

            if (startDate) {
              const formattedStart = this.dateUtils
                .formatIsoToDateOnly(startDate.toISOString())
                .replaceAll('/', '-');
              filenameSuffix += `-${formattedStart}`;
            }

            if (endDate) {
              const formattedEnd = this.dateUtils
                .formatIsoToDateOnly(endDate.toISOString())
                .replaceAll('/', '-');
              filenameSuffix += `-${formattedEnd}`;
            }

            const filename =
              filenameSuffix === ''
                ? 'company-stats-all-time.csv'
                : `company-stats${filenameSuffix}.csv`;

            saveAs(blob, filename);
          }),
          map(() => AdminActions.downloadCompanyStatsCsvSuccess()),
          catchError((error) =>
            of(
              AdminActions.downloadCompanyStatsCsvFailure({
                error: error.message,
              }),
            ),
          ),
        ),
      ),
    ),
  );

  downloadCompaniesStatsForPeriodCsv$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AdminActions.downloadCompaniesStatsCsv),
      switchMap(({ startDate, endDate }) =>
        this.csvService.getCompaniesStatsForPeriodCsv(startDate, endDate).pipe(
          tap((blob) => {
            let filenameSuffix = '';

            if (startDate) {
              const formattedStart = this.dateUtils
                .formatIsoToDateOnly(startDate.toISOString())
                .replaceAll('/', '-');
              filenameSuffix += `-${formattedStart}`;
            }

            if (endDate) {
              const formattedEnd = this.dateUtils
                .formatIsoToDateOnly(endDate.toISOString())
                .replaceAll('/', '-');
              filenameSuffix += `-${formattedEnd}`;
            }

            const filename =
              filenameSuffix === ''
                ? 'companies-stats-all-time.csv'
                : `companies-stats${filenameSuffix}.csv`;

            saveAs(blob, filename);
          }),
          map(() => AdminActions.downloadCompaniesStatsCsvSuccess()),
          catchError((error) =>
            of(
              AdminActions.downloadCompaniesStatsCsvFailure({
                error: error.message,
              }),
            ),
          ),
        ),
      ),
    ),
  );
  downloadMonthlyBillingCsv$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AdminActions.downloadMonthlyBillingCsv),
      switchMap(() =>
        this.csvService.getMonthlyBillingCsv().pipe(
          tap((blob) => saveAs(blob, 'monthly-billing.csv')),
          map(() => AdminActions.downloadMonthlyBillingCsvSuccess()),
          catchError((error) =>
            of(
              AdminActions.downloadMonthlyBillingCsvFailure({
                error: error.message,
              }),
            ),
          ),
        ),
      ),
    ),
  );

  downloadCompanyStatsCsvById$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AdminActions.downloadCompanyStatsCsvById),
      switchMap(({ companyId, companyName, startDate, endDate }) =>
        this.csvService
          .getCompanyStatsByIdForPeriodCsv(companyId, startDate, endDate)
          .pipe(
            tap((blob) => {
              let filenameSuffix = '';
              let hasPeriod = false;

              if (startDate) {
                const formattedStart = this.dateUtils
                  .formatIsoToDateOnly(startDate.toISOString())
                  .replaceAll('/', '-');
                filenameSuffix += `-${formattedStart}`;
                hasPeriod = true;
              }

              if (endDate) {
                const formattedEnd = this.dateUtils
                  .formatIsoToDateOnly(endDate.toISOString())
                  .replaceAll('/', '-');
                filenameSuffix += `-${formattedEnd}`;
                hasPeriod = true;
              }

              const normalizedCompanyName = companyName
                .toLowerCase()
                .replace(/\s+/g, '-')
                .replace(/[^a-z0-9\-]/g, '');

              const filename = `${normalizedCompanyName}-stats${
                hasPeriod ? filenameSuffix : '-all-time'
              }.csv`;

              saveAs(blob, filename);
            }),
            map(() =>
              AdminActions.downloadCompanyStatsCsvByIdSuccess({ companyId }),
            ),
            catchError((error) =>
              of(
                AdminActions.downloadCompanyStatsCsvByIdFailure({
                  companyId,
                  error,
                }),
              ),
            ),
          ),
      ),
    ),
  );
  downloadCompanyMonthlyBillingCsvById$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AdminActions.downloadCompanyMonthlyBillingCsvById),
      switchMap(({ companyId, companyName }) =>
        this.csvService
          .getMonthlyBillingCsvByCompanyId(companyId, companyName)
          .pipe(
            tap((blob) => {
              const normalizedCompanyName = companyName
                .toLowerCase()
                .replace(/\s+/g, '-')
                .replace(/[^a-z0-9\-]/g, '');
              const filename = `monthly-billing-${normalizedCompanyName}.csv`;
              saveAs(blob, filename);
            }),
            map(() =>
              AdminActions.downloadCompanyMonthlyBillingCsvByIdSuccess({
                companyId,
              }),
            ),
            catchError((error) =>
              of(
                AdminActions.downloadCompanyMonthlyBillingCsvByIdFailure({
                  companyId: companyId,
                  error: error.message,
                }),
              ),
            ),
          ),
      ),
    ),
  );
  private billingService = inject(BillingService);
  loadCurrentBilling$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loadCurrentBilling),
      switchMap(() =>
        this.billingService.getCurrentBilling().pipe(
          map((billing) => loadCurrentBillingSuccess({ billing })),
          catchError(() => EMPTY),
        ),
      ),
    ),
  );
  loadSelectedCompanyBilling$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loadSelectedCompanyBilling),
      switchMap(({ id }) =>
        this.billingService.getCurrentBillingByCompanyId(id).pipe(
          map((selectedCompanyBilling) =>
            loadSelectedCompanyBillingSuccess({ selectedCompanyBilling }),
          ),
          catchError(() => EMPTY),
        ),
      ),
    ),
  );

  constructor(private actions$: Actions) {}
}
